import { useCallback, useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import type { ApiClient } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import { LocalTabs } from '../rahmen/LocalTabs';
import {
  ConfirmDialog, ContentSection, ErrorState, ExportLeiste, LoadingState, StatusBadge, Vorbehalte,
  type ExportZiel, type Vorbehalt,
} from '../muster';
import { Uebersicht } from './msr/Uebersicht';
import { Baum } from './msr/Baum';
import { Asps } from './msr/Asps';
import { Datenpunkte } from './msr/Datenpunkte';
import { Funktionen } from './msr/Funktionen';
import { Raumautomation } from './msr/Raumautomation';
import { Schemen } from './msr/Schemen';
import { Aks } from './msr/Aks';
import { Dokumente } from './msr/Dokumente';
import { Grundlagen } from './msr/Grundlagen';
import { Festlegungen } from './msr/Festlegungen';
import { Mbus } from './msr/Mbus';
import { Befunde } from './msr/Befunde';
import { Annahmen, msrAnnahmenVorbehalt } from './msr/Annahmen';
import {
  MsrExportKontext, MsrRechenstandAnzeige, RANG_SEITE, msrZiele, useMsrExport, veraltetVorbehalt, zeitpunkt,
  zieleOrdnen, type MsrExportMelden,
} from './msr/kopf';
import type {
  MsrAksAufbauZeile, MsrAnlageZeile, MsrAspZeile, MsrGrundlagen, MsrLauf,
  MsrRaumZeile, MsrUebersicht, MsrVdiGrundlagen, MsrEinstellwertvorlage,
  MsrFestlegungen, MsrMbus, MsrRechenstand, MsrAnnahmenliste,
} from './msr/typen';

/**
 * Gebäudeautomation (MSR) — Zustand und Lader bleiben in `MsrRahmen`,
 * Teilkomponenten mit Props je Reiter (Muster `Koordination.tsx`,
 * Kopfkommentar dort Z. 15-57). Aktiver Reiter ist seit Paket N2 eine
 * ECHTE Unterroute statt `?reiter=` (Muster N1, Gliederung in
 * `seiten/msr/abschnitte.ts`).
 *
 * --- Warum hier kein `<Fachaktionen>` steht --------------------------------
 *
 * `Fachaktionen` beauftragt eine Revit-Aufnahme (`leseArt`/`schreibArt` sind
 * `ExportKind`-Werte, siehe `Tuv.Export.Domain.Enums.ExportKind`). MSR liest
 * kein eigenes Modell — sein Umfang FOLGT aus den bereits aufgenommenen
 * Gewerken (Abschnitt 1 der Spezifikation). Es gibt dafür keinen `ExportKind`
 * und keinen sinnvollen Wert für `leseArt`. Der Kopf hier ist deshalb eine
 * eigene Kopfzeile mit dem Ableitungslauf statt einer Modellaufnahme.
 *
 * --- Rechenstand und Exportleiste (TM-8a, TX-STANDARD S1.2/S1.9) -----------
 *
 * `meta` ist der Rechenstand aus `GET …/msr/rechenstand` (Lauf, Modellstand
 * der Eingänge, veraltet); darunter der Vorbehalt „veraltet“. `aktionen` ist
 * die Exportleiste der aktiven Unterseite: Jede Unterseite und jede
 * Detailkarte meldet ihre Excel-Ziele über `useMsrExport` (seiten/msr/kopf.tsx).
 *
 * --- Annahmen und Vorgaben (TM-8b, S1.2 Nr. 2, S1.6) -------------------------
 *
 * Unter dem Kopf steht neben „veraltet“ der Vorbehalt „n von m Annahmen sind
 * unbestätigt“ aus `GET …/msr/annahmen` mit Verweis auf den Reiter „Annahmen
 * und Vorgaben“ - dieselbe Liste wie die Blätter „Annahmen“ der Mappen.
 *
 * --- Warum elf Routen und nicht länger `?reiter=` --------------------------
 *
 * `struktur.ts` führte MSR bis Paket N2 ausdrücklich als Sonderfall: die
 * Unterseiten in `seiten/msr/` „steuern keine URL, sondern einen internen
 * Zustandsbaum". Diese Ausnahme ist aufgelöst — jeder Reiter ist jetzt ein
 * eigener Pfad unter `msr/…`, mit `LocalTabs` statt der Knopfzeile aus dem
 * ehemaligen `msr/Reiter.tsx` (entfernt, siehe Diff). Der Ladezustand
 * (`alleNeuLaden`, `ableiten`) bleibt hier EINE Stelle, weil alle elf
 * Unterseiten aus demselben Ableitungslauf stammen und nicht einzeln neu
 * geladen werden müssen, wenn man zwischen ihnen wechselt.
 */
interface Rahmen { api: ApiClient; projekt?: { id: string } }

/** Kontext, den `MsrRahmen` per `Outlet` an jede Unterseite weiterreicht. */
export interface MsrKontext {
  api: ApiClient;
  projektId: string;
  uebersicht: MsrUebersicht | null;
  asps: MsrAspZeile[] | null;
  anlagen: MsrAnlageZeile[] | null;
  raeume: MsrRaumZeile[] | null;
  aksAufbau: MsrAksAufbauZeile[] | null;
  onAksAufbauGespeichert: (a: MsrAksAufbauZeile[]) => void;
  grundlagen: MsrGrundlagen | null;
  vdiGrundlagen: MsrVdiGrundlagen | null;
  einstellwertvorlagen: MsrEinstellwertvorlage[] | null;
  festlegungen: MsrFestlegungen | null;
  mbus: MsrMbus | null;
  annahmen: MsrAnnahmenliste | null;
  annahmenFehler: string | null;
  onAnnahmenNeuLaden: () => void;
  onGrundlagenNeuLaden: () => void;
  onVdiNeuLaden: () => void;
  onEinstellwertvorlagenNeuLaden: () => void;
  onFestlegungenNeuLaden: () => void;
}

export function MsrRahmen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [uebersicht, setUebersicht] = useState<MsrUebersicht | null>(null);
  const [asps, setAsps] = useState<MsrAspZeile[] | null>(null);
  const [anlagen, setAnlagen] = useState<MsrAnlageZeile[] | null>(null);
  const [raeume, setRaeume] = useState<MsrRaumZeile[] | null>(null);
  const [aksAufbau, setAksAufbau] = useState<MsrAksAufbauZeile[] | null>(null);
  const [grundlagen, setGrundlagen] = useState<MsrGrundlagen | null>(null);
  const [vdiGrundlagen, setVdiGrundlagen] = useState<MsrVdiGrundlagen | null>(null);
  const [einstellwertvorlagen, setEinstellwertvorlagen] =
    useState<MsrEinstellwertvorlage[] | null>(null);
  const [festlegungen, setFestlegungen] = useState<MsrFestlegungen | null>(null);
  const [mbus, setMbus] = useState<MsrMbus | null>(null);
  const [annahmen, setAnnahmen] = useState<MsrAnnahmenliste | null>(null);
  /** `undefined` = Abruf läuft, `null` = nicht ladbar (dann steht der Fehler unten). */
  const [rechenstandStand, setRechenstandStand] = useState<MsrRechenstand | null | undefined>(undefined);
  const [ladefehler, setLadefehler] = useState<Record<string, string>>({});

  // Die Exportziele der sichtbaren Unterseite und ihrer Detailkarten, je Stelle.
  const [exporte, setExporte] = useState<Record<string, { rang: number; ziele: ExportZiel[] }>>({});
  const melden: MsrExportMelden = useCallback((stelle, rang, ziele) => {
    setExporte((alt) => {
      const neu = { ...alt };
      if (ziele) neu[stelle] = { rang, ziele };
      else delete neu[stelle];
      return neu;
    });
  }, []);
  const exportZiele = zieleOrdnen(Object.values(exporte));

  const merkeFehler = (was: string) => (e: unknown) => {
    setLadefehler((f) => ({ ...f, [was]: e instanceof Error ? e.message : String(e) }));
    return null;
  };

  const alleNeuLaden = () => {
    if (!projekt) return;

    setLadefehler({});

    api.msrUebersicht(projekt.id).then(setUebersicht)
      .catch((e) => setUebersicht(merkeFehler('Übersicht')(e)));
    api.msrAsps(projekt.id).then(setAsps)
      .catch((e) => setAsps(merkeFehler('ASPs')(e)));
    api.msrAnlagen(projekt.id).then(setAnlagen)
      .catch((e) => setAnlagen(merkeFehler('Anlagen')(e)));
    api.msrRaumautomation(projekt.id).then(setRaeume)
      .catch((e) => setRaeume(merkeFehler('Raumautomation')(e)));
    api.msrAksAufbau(projekt.id).then(setAksAufbau)
      .catch((e) => setAksAufbau(merkeFehler('AKS-Aufbau')(e)));
    api.msrGrundlagen(projekt.id).then(setGrundlagen)
      .catch((e) => setGrundlagen(merkeFehler('Grundlagen')(e)));
    api.msrVdiGrundlagen(projekt.id).then(setVdiGrundlagen)
      .catch((e) => setVdiGrundlagen(merkeFehler('VDI 3814')(e)));
    api.msrEinstellwertvorlagen(projekt.id).then(setEinstellwertvorlagen)
      .catch((e) => setEinstellwertvorlagen(merkeFehler('Einstellwertvorlagen')(e)));
    api.msrFestlegungen(projekt.id).then(setFestlegungen)
      .catch((e) => setFestlegungen(merkeFehler('Festlegungen')(e)));
    api.msrMbus(projekt.id).then(setMbus)
      .catch((e) => setMbus(merkeFehler('M-Bus')(e)));
    api.msrRechenstand(projekt.id).then(setRechenstandStand)
      .catch((e) => setRechenstandStand(merkeFehler('Rechenstand')(e)));
    api.msrAnnahmen(projekt.id).then(setAnnahmen)
      .catch((e) => setAnnahmen(merkeFehler('Annahmen')(e)));
  };

  useEffect(alleNeuLaden, [api, projekt?.id]);

  // ── Ableitungslauf ────────────────────────────────────────────────────
  const [bestaetigenOffen, setBestaetigenOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<MsrLauf | null>(null);
  const [ableitenFehler, setAbleitenFehler] = useState<string | null>(null);

  const ableiten = async () => {
    if (!projekt) return;

    setBestaetigenOffen(false);
    setLaeuft(true);
    setAbleitenFehler(null);
    setErgebnis(null);

    try {
      const lauf = await api.msrAbleiten(projekt.id, true, false);
      setErgebnis(lauf);
      alleNeuLaden();
    } catch (e) {
      setAbleitenFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (!projekt) return <LoadingState text="Projekt wird abgerufen …" />;

  const fehlerBereiche = Object.entries(ladefehler);
  const vorbehalte = [
    msrAnnahmenVorbehalt(annahmen, `/projekte/${projekt.id}/msr/annahmen`),
    veraltetVorbehalt(rechenstandStand),
  ].filter((v): v is Vorbehalt => v !== null);

  const kontext: MsrKontext = {
    api,
    projektId: projekt.id,
    uebersicht,
    asps,
    anlagen,
    raeume,
    aksAufbau,
    onAksAufbauGespeichert: setAksAufbau,
    grundlagen,
    vdiGrundlagen,
    einstellwertvorlagen,
    festlegungen,
    mbus,
    annahmen,
    annahmenFehler: ladefehler.Annahmen ?? null,
    onAnnahmenNeuLaden: () => {
      setLadefehler((f) => { const neu = { ...f }; delete neu.Annahmen; return neu; });
      api.msrAnnahmen(projekt.id).then(setAnnahmen)
        .catch((e) => setAnnahmen(merkeFehler('Annahmen')(e)));
    },
    onGrundlagenNeuLaden: () => { api.msrGrundlagen(projekt.id).then(setGrundlagen); },
    onVdiNeuLaden: () => { api.msrVdiGrundlagen(projekt.id).then(setVdiGrundlagen); },
    onEinstellwertvorlagenNeuLaden: () => {
      api.msrEinstellwertvorlagen(projekt.id).then(setEinstellwertvorlagen);
    },
    onFestlegungenNeuLaden: () => { api.msrFestlegungen(projekt.id).then(setFestlegungen); },
  };

  return (
    <>
      <PageHeader
        titel="Gebäudeautomation (MSR)"
        beschreibung={'Datenpunkte, ASPs, GA-Funktionen und Raumautomation — abgeleitet aus '
          + 'den Gewerkemodulen, VDI 3814 / VDI 3813.'}
        meta={<MsrRechenstandAnzeige stand={rechenstandStand} />}
        aktionen={(
          <>
            {exportZiele.length > 0 && <ExportLeiste ziele={exportZiele} />}
            <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft}
              onClick={() => setBestaetigenOffen(true)}>
              {laeuft ? 'läuft …' : 'Ableitung ausführen'}
            </button>
          </>
        )}
      >
        <LocalTabs
          ariaLabel="Gebäudeautomation (MSR)"
          eintraege={[
            { to: '.', beschriftung: 'Übersicht', end: true },
            { to: 'befunde', beschriftung: 'Befunde' },
            { to: 'baum', beschriftung: 'Baum' },
            { to: 'asps', beschriftung: 'Schaltschränke / ASPs' },
            { to: 'datenpunkte', beschriftung: 'Datenpunktliste' },
            { to: 'funktionen', beschriftung: 'GA-Funktionen' },
            { to: 'raumautomation', beschriftung: 'Raumautomation' },
            { to: 'mbus', beschriftung: 'M-Bus' },
            { to: 'schemen', beschriftung: 'Schemen' },
            { to: 'aks', beschriftung: 'AKS' },
            { to: 'dokumente', beschriftung: 'Dokumente' },
            { to: 'grundlagen', beschriftung: 'Grundlagen' },
            { to: 'annahmen', beschriftung: 'Annahmen und Vorgaben' },
          ]}
        />
      </PageHeader>

      {vorbehalte.length > 0 && <Vorbehalte eintraege={vorbehalte} />}

      {ableitenFehler && (
        <ErrorState
          titel="Der Ableitungslauf ist gescheitert."
          meldung="Die Daten unten stehen deshalb auf dem Stand vor dem Lauf."
          technisch={ableitenFehler}
          beiErneutVersuchen={() => setBestaetigenOffen(true)}
        />
      )}

      {ergebnis && (
        <ContentSection
          titel="Ableitungslauf"
          meta={zeitpunkt(ergebnis.gestartetAm)}
        >
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
            {ergebnis.gegenproben.map((g) => (
              <li key={g.schluessel}>
                <StatusBadge
                  klein
                  zustand={g.ergebnis === 'ok' ? 'gut'
                    : g.ergebnis === 'warnung' ? 'warnung' : 'fehler'}
                  text={g.ergebnis === 'ok' ? 'in Ordnung'
                    : g.ergebnis === 'warnung' ? 'Prüfen' : 'Fehler'}
                />
                {' '}{g.aussage}
              </li>
            ))}
          </ul>
        </ContentSection>
      )}

      <ConfirmDialog
        offen={bestaetigenOffen}
        art="warnung"
        titel="Ableitungslauf ausführen?"
        text="Vergibt AKS-Kennungen. Vergebene Kennungen werden nie geändert."
        bestaetigenText="Ja, ausführen"
        laeuft={laeuft}
        beiBestaetigen={() => void ableiten()}
        beiAbbrechen={() => setBestaetigenOffen(false)}
      />

      {fehlerBereiche.length > 0 && (
        <ErrorState
          titel={`${fehlerBereiche.length} Bereich(e) ließen sich nicht laden.`}
          meldung={'Die Reiter unten sind deshalb leer oder unvollständig — das ist ein '
            + 'Fehler und nicht das Ergebnis. Bitte melden.'}
          technisch={fehlerBereiche.map(([was, text]) => `${was}: ${text}`).join('\n')}
          beiErneutVersuchen={alleNeuLaden}
        />
      )}

      <MsrExportKontext.Provider value={melden}>
        <Outlet context={kontext} />
      </MsrExportKontext.Provider>
    </>
  );
}

export function MsrUebersichtSeite() {
  const { api, projektId, uebersicht } = useOutletContext<MsrKontext>();
  useMsrExport(RANG_SEITE, () => [msrZiele.isp(api, projektId)], [api, projektId]);
  return uebersicht
    ? <Uebersicht u={uebersicht} />
    : <LoadingState zeilen={6} spalten={3} />;
}

export function MsrBaumSeite() {
  const { api, projektId } = useOutletContext<MsrKontext>();
  const [nach, setNach] = useState<'gewerk' | 'asp'>('gewerk');
  return <Baum api={api} projektId={projektId} nach={nach} onNachChange={setNach} />;
}

export function MsrAspsSeite() {
  const { api, projektId, asps, annahmen } = useOutletContext<MsrKontext>();
  // MSR-15: Der Vorbehalt am Kabelabschnitt gilt, solange die Liste das Kennzeichen nicht als bestätigt führt.
  const kabelBestaetigt = annahmen?.zeilen.find((z) => z.schluessel === 'kabelkennzeichen')?.bestaetigt ?? false;
  return asps
    ? <Asps api={api} projektId={projektId} asps={asps} kabelBestaetigt={kabelBestaetigt} />
    : <LoadingState zeilen={6} spalten={3} />;
}

export function MsrDatenpunkteSeite() {
  const { api, projektId, asps, anlagen } = useOutletContext<MsrKontext>();
  return (
    <Datenpunkte api={api} projektId={projektId} asps={asps ?? []} anlagen={anlagen ?? []} />
  );
}

export function MsrFunktionenSeite() {
  const { api, projektId, anlagen } = useOutletContext<MsrKontext>();
  return <Funktionen api={api} projektId={projektId} anlagen={anlagen ?? []} />;
}

export function MsrRaumautomationSeite() {
  const { api, projektId, raeume } = useOutletContext<MsrKontext>();
  return <Raumautomation api={api} projektId={projektId} raeume={raeume ?? []} />;
}

export function MsrSchemenSeite() {
  const { api, projektId, anlagen, asps } = useOutletContext<MsrKontext>();
  return <Schemen api={api} projektId={projektId} anlagen={anlagen ?? []} asps={asps ?? []} />;
}

export function MsrDokumenteSeite() {
  const { api, projektId, anlagen, asps } = useOutletContext<MsrKontext>();
  return <Dokumente api={api} projektId={projektId} anlagen={anlagen ?? []} asps={asps ?? []} />;
}

export function MsrAksSeite() {
  const {
    api, projektId, aksAufbau, vdiGrundlagen, onAksAufbauGespeichert,
  } = useOutletContext<MsrKontext>();
  if (!aksAufbau) return <LoadingState zeilen={6} spalten={3} />;
  return (
    <Aks api={api} projektId={projektId} aufbau={aksAufbau}
      aggregatkatalog={vdiGrundlagen?.aggregatkatalog ?? []}
      onAufbauGespeichert={onAksAufbauGespeichert} />
  );
}

/**
 * Grundlagen — Festlegungen und Grundlagen stehen wie vor Paket N2
 * zusammen unter EINEM Reiter (jetzt: einer Route): Sie teilten sich bei
 * `Msr.tsx` schon immer eine Kopfzeile ("Die Festlegungen stehen VOR den
 * Vorlagen: Sie sind die Grundlage, aus der die Vorlagenwerte folgen").
 */
export function MsrGrundlagenSeite() {
  const {
    api, projektId, festlegungen, grundlagen, vdiGrundlagen, einstellwertvorlagen,
    onFestlegungenNeuLaden, onGrundlagenNeuLaden, onVdiNeuLaden,
    onEinstellwertvorlagenNeuLaden,
  } = useOutletContext<MsrKontext>();
  useMsrExport(RANG_SEITE, () => [msrZiele.effizienz(api, projektId)], [api, projektId]);

  return (
    <>
      {festlegungen && (
        <Festlegungen api={api} projektId={projektId} daten={festlegungen}
          onNeuLaden={onFestlegungenNeuLaden} />
      )}

      {grundlagen
        ? <Grundlagen api={api} projektId={projektId} grundlagen={grundlagen}
            vdiGrundlagen={vdiGrundlagen}
            einstellwertvorlagen={einstellwertvorlagen}
            onNeuLaden={onGrundlagenNeuLaden}
            onVdiNeuLaden={onVdiNeuLaden}
            onEinstellwertvorlagenNeuLaden={onEinstellwertvorlagenNeuLaden} />
        : <LoadingState zeilen={6} spalten={3} />}
    </>
  );
}

export function MsrMbusSeite() {
  const { api, projektId, mbus } = useOutletContext<MsrKontext>();
  useMsrExport(RANG_SEITE, () => [msrZiele.mbus(api, projektId)], [api, projektId]);
  return mbus
    ? <Mbus daten={mbus} />
    : <LoadingState zeilen={6} spalten={3} />;
}

/**
 * Befunde (TM-8a): die projektweite Befundliste - gespeicherte des Laufs und die beim Abruf gebildeten (TM-1) -,
 * Marke nach S1.5. Die vollständige Liste steht in der Excel-Mappe „Befunde“.
 */
export function MsrBefundeSeite() {
  const { api, projektId } = useOutletContext<MsrKontext>();
  useMsrExport(RANG_SEITE, () => [msrZiele.befunde(api, projektId)], [api, projektId]);
  return (
    <Befunde api={api} projektId={projektId} titel="Befunde"
      leerText="Keine Befunde der Gebäudeautomation." />
  );
}

/**
 * Annahmen und Vorgaben (TM-8b, S1.6): die Liste aus `GET …/msr/annahmen`, im Rahmen geladen (der Kopf zeigt daraus
 * den Vorbehalt). Bestätigt wird hier nichts.
 */
export function MsrAnnahmenSeite() {
  const { annahmen, annahmenFehler, onAnnahmenNeuLaden } = useOutletContext<MsrKontext>();
  return <Annahmen liste={annahmen} fehler={annahmenFehler} beiErneutVersuchen={onAnnahmenNeuLaden} />;
}
